/**
 * Correcteur orthographique local, sous chaque zone de texte multi-ligne.
 *
 * <p>Le bouton « ✨ Corriger » reprend le correcteur de la maquette : il ne
 * passe par aucun service et n'envoie rien sur le réseau. Il corrige les fautes
 * courantes d'une saisie sur téléphone (accents oubliés du vocabulaire du
 * bâtiment), remet une majuscule en début de phrase et range les espaces :
 * jamais deux espaces de suite, aucun espace avant la ponctuation — l'usage
 * courant en Suisse romande, où l'espace fine ne se tape pas au clavier.
 *
 * <p>Ce n'est pas la rédaction par IA (`BoutonRediger`, payante) : ici, aucune
 * phrase n'est réécrite.
 */

/** Fautes courantes : mot sans accent → mot correct (en minuscules). */
export const CORR_DICT = {
  reparation: 'réparation',
  reparations: 'réparations',
  reparer: 'réparer',
  demolition: 'démolition',
  demolir: 'démolir',
  electricite: 'électricité',
  electrique: 'électrique',
  electricien: 'électricien',
  beton: 'béton',
  platre: 'plâtre',
  platrerie: 'plâtrerie',
  maconnerie: 'maçonnerie',
  macon: 'maçon',
  fenetre: 'fenêtre',
  fenetres: 'fenêtres',
  cle: 'clé',
  metre: 'mètre',
  metres: 'mètres',
  carre: 'carré',
  carres: 'carrés',
  cout: 'coût',
  materiel: 'matériel',
  materiaux: 'matériaux',
  recu: 'reçu',
  facon: 'façon',
  controle: 'contrôle',
  controler: 'contrôler',
  renovation: 'rénovation',
  renover: 'rénover',
  systeme: 'système',
  reglage: 'réglage',
  depose: 'dépose',
  cablage: 'câblage',
  cable: 'câble',
  etancheite: 'étanchéité',
  interieur: 'intérieur',
  exterieur: 'extérieur',
  degat: 'dégât',
  degats: 'dégâts',
  evacuation: 'évacuation',
  reseau: 'réseau',
  echafaudage: 'échafaudage',
  securite: 'sécurité',
  proprete: 'propreté',
  batiment: 'bâtiment',
  numero: 'numéro',
  repare: 'réparé',
  prepare: 'préparé',
  termine: 'terminé',
  livre: 'livré',
  achete: 'acheté',
  enleve: 'enlevé',
  installe: 'installé',
  cree: 'créé',
  verifie: 'vérifié',
  nettoye: 'nettoyé',
  pieces: 'pièces',
  piece: 'pièce',
  apres: 'après',
  tres: 'très',
  deja: 'déjà',
  // Mots du métier mal écrits ou mal dictés (« Réparation des plaintes »).
  plainte: 'plinthe',
  plaintes: 'plinthes',
  plinte: 'plinthe',
  plintes: 'plinthes',
  crepit: 'crépi',
  crépit: 'crépi',
  faillance: 'faïence',
  faiance: 'faïence',
  faience: 'faïence',
  fayence: 'faïence',
  carlage: 'carrelage',
  carelage: 'carrelage',
  carrellage: 'carrelage',
  sillicone: 'silicone',
  silicon: 'silicone',
  chappe: 'chape',
  isolassion: 'isolation',
  plafon: 'plafond',
  plaffond: 'plafond',
  cloisson: 'cloison',
  robinnet: 'robinet',
  goutiere: 'gouttière',
  goutière: 'gouttière',
  gouttiere: 'gouttière',
  ragreage: 'ragréage',
  parket: 'parquet',
  placoplatre: 'placoplâtre',
  echaffaudage: 'échafaudage',
  échaffaudage: 'échafaudage',
  étancheité: 'étanchéité',
  evier: 'évier',
};

/**
 * Formes d'un verbe au présent, justes telles quelles (« il installe »,
 * « livre et pose ») : la correction automatique ne les touche pas, seul le
 * bouton « ✨ Corriger » les accentue.
 */
export const AMBIGUS = new Set([
  'repare',
  'prepare',
  'termine',
  'livre',
  'achete',
  'enleve',
  'installe',
  'verifie',
  'depose',
  'controle',
  'cable',
  'piece',
]);

/*
 * Limites de mot sensibles aux lettres accentuées : le `\b` de JavaScript ne
 * connaît que l'ASCII et verrait une fin de mot entre « cl » + « e » et « é »
 * de « clé » — une seconde correction donnerait « cléé ». Ici, un mot déjà
 * juste n'est jamais retouché.
 */
const REGLES = Object.keys(CORR_DICT).map((faute) => ({
  faute,
  motif: new RegExp('(?<![\\p{L}\\p{N}_])' + faute + '(?![\\p{L}\\p{N}_])', 'giu'),
  juste: CORR_DICT[faute],
}));

/** Garde la majuscule initiale d'un mot corrigé (« Beton » → « Béton »). */
const memeCasse = (trouve, juste) =>
  trouve.charAt(0) === trouve.charAt(0).toUpperCase()
    ? juste.charAt(0).toUpperCase() + juste.slice(1)
    : juste;

/**
 * Corrige un texte libre. Rend la valeur reçue telle quelle si elle est vide.
 *
 * <p>Règles, dans l'ordre : fautes du dictionnaire ; espaces multiples réduits
 * à un seul ; espaces avant `. , ; : ! ?` retirés ; majuscule au début du
 * texte et après `.`, `!` ou `?`.
 */
export const corrigerFr = (txt) => {
  if (!txt) return txt;
  let t = String(txt);
  for (const { motif, juste } of REGLES) {
    t = t.replace(motif, (m) => memeCasse(m, juste));
  }
  t = t.replace(/ {2,}/g, ' ').replace(/ +([.,;:!?])/g, '$1');
  t = t.replace(/(^\s*|[.!?]\s+)([a-zà-ÿ])/g, (m, avant, lettre) => avant + lettre.toUpperCase());
  return t;
};

/** Règles appliquées toutes seules à la sortie d'un champ : jamais un mot ambigu. */
const REGLES_AUTO = REGLES.filter((r) => !AMBIGUS.has(r.faute));

/**
 * Corrige toute seule une désignation ou une description, sans rien changer
 * d'autre : ni les espaces, ni les majuscules, ni les retours à la ligne.
 *
 * @param {string} txt texte saisi
 * @returns {{texte: string, corrections: [string, string][]}} le texte corrigé
 *   et les mots remplacés (avant, après)
 */
export const corrigeTout = (txt) => {
  const corrections = [];
  if (!txt) return { texte: txt, corrections };
  let t = String(txt);
  for (const { motif, juste } of REGLES_AUTO) {
    t = t.replace(motif, (m) => {
      const bon = memeCasse(m, juste);
      if (!corrections.some(([a]) => a === m)) corrections.push([m, bon]);
      return bon;
    });
  }
  return { texte: t, corrections };
};
